import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SEO from '../components/SEO';
import { useDestinations } from '../hooks/useDestinations';
import { toUploadUrl } from '../config/api';

const Destinations = () => {
    const location = useLocation();
    const [selectedRegion, setSelectedRegion] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDest, setSelectedDest] = useState(null);
    const [activeImage, setActiveImage] = useState(0);
    
    const { destinations, loading, error, setParams } = useDestinations();
    const regions = [
        { id: 'all', label: 'All Regions', icon: 'fas fa-globe-asia' },
        { id: 'kashmir', label: 'Kashmir', icon: 'fas fa-mountain' },
        { id: 'ladakh', label: 'Ladakh', icon: 'fas fa-road' },
        { id: 'himachal', label: 'Himachal', icon: 'fas fa-tree' },
        { id: 'uttarakhand', label: 'Uttarakhand', icon: 'fas fa-water' }
    ];

    // Parse URL parameters on mount
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const regionParam = params.get('region');
        const searchParam = params.get('search');

        if (regionParam && regionParam !== 'all') setSelectedRegion(regionParam);
        if (searchParam) setSearchTerm(searchParam);
    }, [location]);

    useEffect(() => {
        const params = {};
        if (selectedRegion !== 'all') params.region = selectedRegion;
        if (searchTerm) params.search = searchTerm;
        setParams(params);
    }, [selectedRegion, searchTerm, setParams]);

    // Helper function to get image URL
    const getImageUrl = (imagePath) => {
        return toUploadUrl(imagePath, {
            fallback: 'https://placehold.co/400x300/1a1a1a/ffffff?text=No+Image'
        });
    };

    if (loading) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading amazing destinations...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="error-container">
                <i className="fas fa-exclamation-circle"></i>
                <h3>Something went wrong</h3>
                <p>{error}</p>
                <button onClick={() => window.location.reload()} className="retry-btn">
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="destinations-page">
            <SEO
                title="Destinations | RedGoTrip"
                description="Explore destination guides, best time to visit, travel highlights and holiday ideas across RedGoTrip's curated destinations."
                canonicalPath={`/destinations${location.search || ''}`}
                keywords="travel destinations, destination guides, best time to visit, Kashmir, Himachal, Ladakh, RedGoTrip"
            />
            <section className="destinations-hero">
                <div className="hero-content">
                    <span className="destinations-badge">Explore Scenic India</span>
                    <h1>Discover India's <span className="highlight">Most Beautiful Destinations</span></h1>
                    <p>Browse handpicked mountain escapes, lakeside retreats and culture-rich places for your next holiday.</p>
                    <div className="hero-stats-strip">
                        <div className="hero-stat-box">
                            <strong>{destinations.length || 0}+</strong>
                            <span>Destinations</span>
                        </div>
                        <div className="hero-stat-box">
                            <strong>4.7/5</strong>
                            <span>Traveler Rating</span>
                        </div>
                        <div className="hero-stat-box">
                            <strong>All Seasons</strong>
                            <span>Travel Options</span>
                        </div>
                    </div>
                </div>
            </section>

            <section className="destination-controls-section">
                <div className="container">
                    <div className="destination-controls-card">
                        <div className="destination-search-wrap">
                            <i className="fas fa-search"></i>
                            <input
                                type="text"
                                placeholder="Search destinations by name or keyword"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="destination-search-input"
                            />
                        </div>

                        <div className="destination-region-pills">
                            {regions.map((region) => (
                                <button
                                    key={region.id}
                                    className={`region-pill ${selectedRegion === region.id ? 'active' : ''}`}
                                    onClick={() => setSelectedRegion(region.id)}
                                >
                                    <i className={region.icon}></i>
                                    {region.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section className="destinations-grid-section">
                <div className="container">
                    <div className="destinations-results-bar">
                        <div>
                            <h2>Browse Destinations</h2>
                            <p>{destinations.length} {destinations.length === 1 ? 'destination' : 'destinations'} available for your next trip</p>
                        </div>
                        {(selectedRegion !== 'all' || searchTerm) && (
                            <button
                                className="reset-destination-filters"
                                onClick={() => {
                                    setSelectedRegion('all');
                                    setSearchTerm('');
                                }}
                            >
                                Reset Filters
                            </button>
                        )}
                    </div>

                    {destinations.length > 0 ? (
                        <div className="destinations-grid">
                            {destinations.map(destination => (
                                <div key={destination.id} className="destination-card">
                                    <div className="card-image">
                                        <img 
                                            src={getImageUrl(destination.image)} 
                                            alt={destination.name}
                                            loading="lazy"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.src = 'https://placehold.co/400x300/1a1a1a/ffffff?text=No+Image';
                                            }}
                                        />
                                        <div className="image-overlay">
                                            <button 
                                                className="quick-view-btn"
                                                onClick={() => {
                                                    setSelectedDest(destination);
                                                    setActiveImage(0);
                                                }}
                                            >
                                                <i className="fas fa-eye"></i> Quick View
                                            </button>
                                        </div>
                                        <div className="card-badges">
                                            <span className="rating-badge">
                                                <i className="fas fa-star"></i> {destination.rating || '4.5'}
                                            </span>
                                            <span className="review-badge">
                                                <i className="fas fa-user"></i> {destination.reviews || 0}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="card-content">
                                        <div className="destination-card-heading">
                                            <div>
                                                <h3>{destination.name}</h3>
                                                <p className="destination-title">
                                                    <i className="fas fa-tag"></i> {destination.title || 'Beautiful Destination'}
                                                </p>
                                            </div>
                                                
                                        </div>

                                        <p className="destination-desc">
                                            {destination.description?.substring(0, 110) || 'A beautiful destination worth exploring.'}
                                            {destination.description?.length > 110 ? '...' : ''}
                                        </p>
                                        
                                        <div className="destination-meta">
                                            <div className="meta-item">
                                                <i className="fas fa-clock"></i>
                                                <span>{destination.ideal_duration || '2-3 days'}</span>
                                            </div>
                                            <div className="meta-item">
                                                <i className="fas fa-calendar-alt"></i>
                                                <span>{destination.best_time || 'All year'}</span>
                                            </div>
                                            <div className="meta-item">
                                                <i className="fas fa-mountain"></i>
                                                <span>{destination.altitude || 'N/A'}</span>
                                            </div>
                                        </div>

                                        <div className="highlights-preview">
                                            {destination.highlights?.slice(0, 3).map((item, idx) => (
                                                <span key={idx} className="highlight-tag">
                                                    <i className="fas fa-check-circle"></i> {item}
                                                </span>
                                            ))}
                                        </div>

                                        <div className="card-footer">
                                            <Link to={`/packages?destination=${destination.region || destination.name.toLowerCase()}`} className="view-packages-btn">
                                                View Packages <i className="fas fa-arrow-right"></i>
                                            </Link>
                                            <Link to={`/destinations/${destination.id}`} className="details-link">
                                                Details
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-results">
                            <i className="fas fa-map-marked-alt"></i>
                            <h3>No destinations found</h3>
                            <p>Try adjusting your search or filter criteria</p>
                            <button 
                                className="reset-btn"
                                onClick={() => {
                                    setSelectedRegion('all');
                                    setSearchTerm('');
                                }}
                            >
                                Reset Filters
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Destination Quick View Modal */}
            {selectedDest && (
                <div className="modal-overlay" onClick={() => setSelectedDest(null)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <button className="modal-close" onClick={() => setSelectedDest(null)}>
                            <i className="fas fa-times"></i>
                        </button>
                        
                        <div className="modal-gallery">
                            <div className="main-image">
                                <img
                                    src={
                                        selectedDest?.gallery?.length > 0 && selectedDest.gallery[activeImage]
                                            ? getImageUrl(selectedDest.gallery[activeImage])
                                            : getImageUrl(selectedDest?.image)
                                    }
                                    alt={selectedDest?.name || "Destination"}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = "https://placehold.co/800x400/1a1a1a/ffffff?text=No+Image";
                                    }}
                                />
                                {selectedDest?.gallery?.length > 1 && (
                                    <>
                                        <button 
                                            className="gallery-nav prev"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveImage(prev => 
                                                    prev === 0 ? selectedDest.gallery.length - 1 : prev - 1
                                                );
                                            }}
                                        >
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                        <button 
                                            className="gallery-nav next"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveImage(prev => 
                                                    prev === selectedDest.gallery.length - 1 ? 0 : prev + 1
                                                );
                                            }}
                                        >
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                    </>
                                )}
                            </div>

                            {selectedDest?.gallery?.length > 1 && (
                                <div className="thumbnail-grid">
                                    {selectedDest.gallery.map((img, idx) => (
                                        <div
                                            key={idx}
                                            className={`thumbnail ${activeImage === idx ? "active" : ""}`}
                                            onClick={() => setActiveImage(idx)}
                                        >
                                            <img
                                                src={getImageUrl(img)}
                                                alt={`${selectedDest?.name || "Image"} ${idx + 1}`}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'https://placehold.co/150x150/1a1a1a/ffffff?text=No+Image';
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="modal-info">
                            <h2>{selectedDest.name}</h2>
                            <p className="modal-title">{selectedDest.title || 'Beautiful Destination'}</p>
                            
                            <div className="modal-rating">
                                <div className="stars">
                                    {[...Array(5)].map((_, i) => (
                                        <i 
                                            key={i} 
                                            className={`fas fa-star ${i < Math.floor(selectedDest.rating || 4.5) ? 'filled' : ''}`}
                                            style={{ color: i < Math.floor(selectedDest.rating || 4.5) ? '#ffc107' : '#e2e8f0' }}
                                        ></i>
                                    ))}
                                </div>
                                <span>{selectedDest.rating || '4.5'} ({selectedDest.reviews || 0} reviews)</span>
                            </div>

                            <p className="modal-description">{selectedDest.long_description || selectedDest.description || 'No description available.'}</p>

                            <div className="modal-details">
                                <div className="detail-item">
                                    <i className="fas fa-clock"></i>
                                    <div>
                                        <strong>Ideal Duration</strong>
                                        <span>{selectedDest.ideal_duration || '2-3 days'}</span>
                                    </div>
                                </div>
                                <div className="detail-item">
                                    <i className="fas fa-calendar-alt"></i>
                                    <div>
                                        <strong>Best Time</strong>
                                        <span>{selectedDest.best_time || 'All year'}</span>
                                    </div>
                                </div>
                                <div className="detail-item">
                                    <i className="fas fa-mountain"></i>
                                    <div>
                                        <strong>Altitude</strong>
                                        <span>{selectedDest.altitude || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>

                            {selectedDest.highlights && selectedDest.highlights.length > 0 && (
                                <div className="modal-highlights">
                                    <h3>Highlights</h3>
                                    <div className="highlights-list">
                                        {selectedDest.highlights.map((item, idx) => (
                                            <span key={idx} className="highlight-badge">
                                                <i className="fas fa-check-circle"></i> {item}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {selectedDest.activities && selectedDest.activities.length > 0 && (
                                <div className="modal-activities">
                                    <h3>Activities</h3>
                                    <ul>
                                        {selectedDest.activities.slice(0, 6).map((activity, idx) => (
                                            <li key={idx}>
                                                <i className="fas fa-check"></i> {activity}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="modal-packages">
                                <Link to={`/packages?destination=${selectedDest.region || selectedDest.name.toLowerCase()}`} className="view-all-packages">
                                    View Packages in {selectedDest.name} <i className="fas fa-arrow-right"></i>
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
    
};


export default Destinations;
